/**
 * SprintGauge - Colorful arcade-style sprint multiplier gauge
 *
 * A fun, joyful gauge that fills up as the player sprints,
 * with rainbow colors, sparkles, and bouncy animations!
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { SPRINT_MULTIPLIER_TIERS } from '../offline-config';
import { FREE_SPRINT_SCORE_THRESHOLD } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

/**
 * SprintGauge configuration
 */
export interface SprintGaugeConfig {
  /** Width of the gauge bar */
  width: number;
  /** Height of the gauge bar */
  height: number;
  /** Padding from edge */
  padding: number;
}

const DEFAULT_CONFIG: SprintGaugeConfig = {
  width: 28,
  height: 180,
  padding: 15,
};

/** Type for a sprint multiplier tier */
type SprintTier = typeof SPRINT_MULTIPLIER_TIERS[number];

/** Rainbow colors for the gauge */
const RAINBOW_COLORS = [
  0x00ffaa, // Mint green (bottom)
  0x00ff00, // Green
  0xaaff00, // Lime
  0xffff00, // Yellow
  0xffaa00, // Orange
  0xff5500, // Red-orange
  0xff00aa, // Pink
  0xaa00ff, // Purple (top)
];

/** Number of segments in the gauge fill */
const GAUGE_SEGMENT_COUNT = 20;

/** Progress thresholds for visual feedback */
const PROGRESS_THRESHOLDS = {
  /** When to start showing excitement effects */
  EXCITEMENT_START: 0.6,
  /** When to change to next tier color */
  CLOSE: 0.7,
  /** When to show "almost there" indicators */
  ALMOST_THERE: 0.8,
  /** When to show maximum excitement/flashing */
  NEARLY_COMPLETE: 0.9,
} as const;

/** Animation timing configuration */
const ANIMATION_CONFIG = {
  /** Fade in/out speed */
  FADE_SPEED: 4,
  /** Main pulse speed */
  PULSE_SPEED: 4,
  /** Fast pulse speed for excitement */
  FAST_PULSE_SPEED: 8,
  /** Text bounce animation speed */
  TEXT_BOUNCE_SPEED: 6,
  /** Celebration animation speed */
  CELEBRATION_SPEED: 20,
  /** Sparkle twinkle speed */
  SPARKLE_SPEED: 10,
} as const;

/** Multiplier thresholds for color selection */
const MULTIPLIER_COLOR_THRESHOLDS = {
  PURPLE: 3.0,
  PINK: 2.0,
  RED_ORANGE: 1.8,
  ORANGE: 1.6,
  YELLOW: 1.4,
  LIME: 1.2,
} as const;

/**
 * Get the current tier based on sprint time
 */
export function getSprintTier(sprintTime: number): {
  current: SprintTier;
  next: SprintTier | null;
  progress: number;
} {
  let currentTier: SprintTier = SPRINT_MULTIPLIER_TIERS[0]!;
  let nextTier: SprintTier | null = SPRINT_MULTIPLIER_TIERS[1] ?? null;

  for (let i = SPRINT_MULTIPLIER_TIERS.length - 1; i >= 0; i--) {
    const tier = SPRINT_MULTIPLIER_TIERS[i]!;
    if (sprintTime >= tier.threshold) {
      currentTier = tier;
      nextTier = SPRINT_MULTIPLIER_TIERS[i + 1] ?? null;
      break;
    }
  }

  // Calculate progress to next tier (0-1)
  let progress = 0;
  if (nextTier) {
    const rangeStart = currentTier.threshold;
    const rangeEnd = nextTier.threshold;
    progress = (sprintTime - rangeStart) / (rangeEnd - rangeStart);
  } else {
    progress = 1; // At max tier
  }

  return { current: currentTier, next: nextTier, progress: Math.min(1, Math.max(0, progress)) };
}

/**
 * SprintGauge - Fun arcade-style gauge with rainbow colors and sparkles
 */
export class SprintGauge {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: SprintGaugeConfig;

  /** Background graphics */
  private background: Graphics;

  /** Fill graphics (the actual gauge) */
  private fill: Graphics;

  /** Sparkle/effects graphics */
  private effects: Graphics;

  /** Tier marker graphics */
  private markers: Graphics;

  /** Current multiplier text */
  private multiplierText: Text;

  /** "BOOST" label */
  private labelText: Text;

  /** "FREE SPRINT" indicator text */
  private freeSprintText: Text;

  /** "Time to next tier" text (e.g., "2.3s → 1.4x") */
  private nextTierText: Text;

  /** Persistent global multiplier badge (always visible when > 1x) */
  private globalMultiplierBadge: Container;
  private badgeBackground: Graphics;
  private badgeText: Text;

  /** Target line graphics (pulsing line at next tier threshold) */
  private targetLine: Graphics;

  /** Rocket/flame icon */
  private rocketIcon: Graphics;

  /** Current sprint time */
  private sprintTime: number = 0;

  /** Whether the gauge should be visible */
  private shouldShow: boolean = false;

  /** Fade alpha */
  private alpha: number = 0;

  /** Animation time */
  private animTime: number = 0;

  /** Previous tier for detecting tier-ups */
  private prevTier: number = 1;

  /** Tier-up celebration timer */
  private celebrationTimer: number = 0;

  /** Whether free sprint is currently active */
  private hasFreeSprint: boolean = false;

  /** Animation time for free sprint text */
  private freeSprintAnimTime: number = 0;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  constructor(config: Partial<SprintGaugeConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();

    this.container = new Container();
    this.container.label = 'sprint-gauge';

    // Create graphics layers
    this.background = new Graphics();
    this.effects = new Graphics();
    this.fill = new Graphics();
    this.markers = new Graphics();
    this.targetLine = new Graphics();
    this.rocketIcon = new Graphics();

    // Create multiplier text (shows current multiplier like "1.4x")
    const multiplierStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(22),
      fontWeight: 'bold',
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 4 },
      dropShadow: {
        color: 0x000000,
        blur: 4,
        distance: 2,
        alpha: 0.5,
      },
    });
    this.multiplierText = new Text({ text: '1x', style: multiplierStyle });
    this.multiplierText.anchor.set(0.5, 0.5);

    // Create "BOOST" label with fun style
    const labelStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(11),
      fontWeight: 'bold',
      fill: 0x00ffaa,
      letterSpacing: 2,
      stroke: { color: 0x000000, width: 2 },
    });
    this.labelText = new Text({ text: 'BOOST', style: labelStyle });
    this.labelText.anchor.set(0.5, 0);

    // Create "FREE SPRINT" indicator with exciting style
    const freeSprintStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(14),
      fontWeight: 'bold',
      fill: 0x00ff00, // Bright green
      letterSpacing: 1,
      stroke: { color: 0x000000, width: 3 },
      dropShadow: {
        color: 0x00ff00,
        blur: 8,
        distance: 0,
        alpha: 0.5,
      },
    });
    this.freeSprintText = new Text({ text: 'FREE!', style: freeSprintStyle });
    this.freeSprintText.anchor.set(0.5, 0.5);
    this.freeSprintText.visible = false;

    // Create "time to next tier" text with fun arcade style
    const nextTierStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(12),
      fontWeight: 'bold',
      fill: 0xffff00, // Yellow
      letterSpacing: 0,
      stroke: { color: 0x000000, width: 2 },
      dropShadow: {
        color: 0x000000,
        blur: 3,
        distance: 1,
        alpha: 0.4,
      },
    });
    this.nextTierText = new Text({ text: '', style: nextTierStyle });
    this.nextTierText.anchor.set(0.5, 0);
    this.nextTierText.visible = false;

    // Create persistent global multiplier badge (always visible when > 1x)
    this.globalMultiplierBadge = new Container();
    this.globalMultiplierBadge.label = 'global-multiplier-badge';

    this.badgeBackground = new Graphics();
    const badgeTextStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(16),
      fontWeight: 'bold',
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
      dropShadow: {
        color: 0x000000,
        blur: 4,
        distance: 2,
        alpha: 0.5,
      },
    });
    this.badgeText = new Text({ text: '1x', style: badgeTextStyle });
    this.badgeText.anchor.set(0.5, 0.5);

    this.globalMultiplierBadge.addChild(this.badgeBackground);
    this.globalMultiplierBadge.addChild(this.badgeText);
    this.globalMultiplierBadge.visible = false;

    // Add to container in order (back to front)
    this.container.addChild(this.background);
    this.container.addChild(this.effects);
    this.container.addChild(this.fill);
    this.container.addChild(this.markers);
    this.container.addChild(this.targetLine);
    this.container.addChild(this.rocketIcon);
    this.container.addChild(this.multiplierText);
    this.container.addChild(this.labelText);
    this.container.addChild(this.nextTierText);
    this.container.addChild(this.freeSprintText);
    this.container.addChild(this.globalMultiplierBadge);

    this.drawBackground();
    this.drawMarkers();

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all text elements
   */
  private applyScaling(): void {
    (this.multiplierText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(22);
    (this.labelText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(11);
    (this.freeSprintText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(14);
    (this.nextTierText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(12);
    (this.badgeText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(16);
  }

  /**
   * Draw the background of the gauge - fun bubble style
   */
  private drawBackground(): void {
    const g = this.background;
    const { width, height } = this.config;

    g.clear();

    // Outer glow
    g.roundRect(-width / 2 - 8, -8, width + 16, height + 16, 12);
    g.fill({ color: 0x00ffaa, alpha: 0.15 });

    // Main frame with gradient-like effect
    g.roundRect(-width / 2 - 4, -4, width + 8, height + 8, 10);
    g.fill({ color: 0x1a1a2e, alpha: 0.95 });
    g.stroke({ color: 0x00ffaa, width: 3, alpha: 0.8 });

    // Inner area with subtle gradient
    g.roundRect(-width / 2, 0, width, height, 6);
    g.fill({ color: 0x0a0a1a, alpha: 0.9 });

    // Inner highlight (top)
    g.roundRect(-width / 2 + 2, 2, width - 4, 20, 4);
    g.fill({ color: 0xffffff, alpha: 0.05 });
  }

  /**
   * Draw tier markers with fun star icons
   * Markers are evenly spaced to match the new fill system
   */
  private drawMarkers(): void {
    const g = this.markers;
    const { width, height } = this.config;

    g.clear();

    const totalTiers = SPRINT_MULTIPLIER_TIERS.length;
    const tierHeight = height / (totalTiers - 1); // Each tier gets equal space

    // Draw marker lines and stars for each tier (skip first tier at bottom)
    for (let i = 1; i < totalTiers; i++) {
      const tier = SPRINT_MULTIPLIER_TIERS[i]!;
      const y = height - (i * tierHeight);

      // Get color for this tier level
      const colorIndex = Math.min(i, RAINBOW_COLORS.length - 1);
      const color = RAINBOW_COLORS[colorIndex] ?? 0x00ffaa;

      // Tick mark
      g.moveTo(-width / 2 - 6, y);
      g.lineTo(-width / 2 + 2, y);
      g.stroke({ color, width: 2, alpha: 0.8 });

      // Small star at each tier
      this.drawMiniStar(g, -width / 2 - 12, y, 4, color, 0.7);

      // Add small label showing multiplier
      // (Only for key tiers to avoid clutter)
      if (i === 1 || tier.multiplier >= 2.0) {
        g.moveTo(width / 2 + 2, y);
        g.lineTo(width / 2 + 6, y);
        g.stroke({ color, width: 2, alpha: 0.6 });
      }
    }
  }

  /**
   * Draw a small star shape
   */
  private drawMiniStar(g: Graphics, cx: number, cy: number, size: number, color: number, alpha: number): void {
    const points = 4;
    const innerR = size * 0.4;
    const outerR = size;

    g.moveTo(cx + outerR, cy);
    for (let i = 1; i <= points * 2; i++) {
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      g.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
    }
    g.closePath();
    g.fill({ color, alpha });
  }

  /**
   * Draw the rocket/flame icon at the fill level
   */
  private drawRocket(fillHeight: number, color: number): void {
    const g = this.rocketIcon;
    const { width, height } = this.config;

    g.clear();

    if (fillHeight <= 0) return;

    const topY = height - fillHeight;
    const centerX = 0;

    // Bouncy animation
    const bounce = Math.sin(this.animTime * 8) * 2;
    const rocketY = topY - 8 + bounce;

    // Flame trail (animated)
    const flameHeight = 12 + Math.sin(this.animTime * 15) * 4;
    const flameWidth = width * 0.4;

    // Outer flame (orange/yellow)
    g.moveTo(centerX, rocketY + 8);
    g.lineTo(centerX - flameWidth, rocketY + 8 + flameHeight);
    g.lineTo(centerX, rocketY + 8 + flameHeight * 0.6);
    g.lineTo(centerX + flameWidth, rocketY + 8 + flameHeight);
    g.closePath();
    g.fill({ color: 0xff6600, alpha: 0.9 });

    // Inner flame (yellow/white)
    g.moveTo(centerX, rocketY + 8);
    g.lineTo(centerX - flameWidth * 0.5, rocketY + 8 + flameHeight * 0.7);
    g.lineTo(centerX, rocketY + 8 + flameHeight * 0.4);
    g.lineTo(centerX + flameWidth * 0.5, rocketY + 8 + flameHeight * 0.7);
    g.closePath();
    g.fill({ color: 0xffff00, alpha: 0.9 });

    // Rocket body
    const rocketSize = 10;
    g.circle(centerX, rocketY, rocketSize);
    g.fill({ color, alpha: 1 });
    g.stroke({ color: 0xffffff, width: 2, alpha: 0.8 });

    // Rocket highlight
    g.circle(centerX - 3, rocketY - 3, 3);
    g.fill({ color: 0xffffff, alpha: 0.6 });

    // Arrow pointing up
    g.moveTo(centerX, rocketY - 5);
    g.lineTo(centerX - 4, rocketY + 2);
    g.lineTo(centerX + 4, rocketY + 2);
    g.closePath();
    g.fill({ color: 0xffffff, alpha: 0.9 });
  }

  /**
   * Update the gauge fill with rainbow gradient
   * Shows TWO fills: base tier progress + animated progress to next tier
   */
  private updateFill(): void {
    const g = this.fill;
    const { width, height } = this.config;

    g.clear();

    const { current, next, progress } = getSprintTier(this.sprintTime);

    // Calculate tier index (0-6 for our 7 tiers)
    const tierIndex = SPRINT_MULTIPLIER_TIERS.findIndex(t => t.threshold === current.threshold);
    const totalTiers = SPRINT_MULTIPLIER_TIERS.length;

    // BASE FILL: Show completed tiers as solid fill
    // Each tier takes up an equal portion of the gauge
    const tierHeight = height / (totalTiers - 1); // -1 because first tier is baseline
    const baseFillHeight = tierIndex * tierHeight;

    // PROGRESS FILL: Show progress toward next tier
    const progressFillHeight = next ? progress * tierHeight : 0;

    const totalFillHeight = baseFillHeight + progressFillHeight;

    if (totalFillHeight <= 0 && this.sprintTime <= 0) return;

    // Always show at least a small amount when sprinting starts
    const minVisibleHeight = this.sprintTime > 0 ? Math.max(totalFillHeight, 8) : 0;

    // Draw rainbow segments from bottom to current fill
    const segmentCount = GAUGE_SEGMENT_COUNT;
    const segmentHeight = height / segmentCount;

    for (let i = 0; i < segmentCount; i++) {
      const segmentBottom = height - (i * segmentHeight);
      const segmentTop = segmentBottom - segmentHeight;

      // Skip segments above fill level
      if (segmentTop > height - minVisibleHeight) continue;

      // Get color from rainbow based on tier, not segment
      // Each tier range gets its color
      const segmentTierIndex = Math.floor((i / segmentCount) * (totalTiers - 1));
      const colorIndex = Math.min(segmentTierIndex, RAINBOW_COLORS.length - 1);
      const color = RAINBOW_COLORS[colorIndex] ?? 0x00ffaa;

      // Animated shimmer - more intense for the progress portion
      const isProgressPortion = segmentBottom > height - baseFillHeight;
      const shimmerBase = isProgressPortion ? 0.7 : 0.85;
      const shimmerAmount = isProgressPortion ? 0.3 : 0.15;
      const shimmer = Math.sin(this.animTime * 4 + i * 0.5) * shimmerAmount + shimmerBase;

      // Clip to fill height
      const drawTop = Math.max(segmentTop, height - minVisibleHeight);
      const drawHeight = segmentBottom - drawTop;

      if (drawHeight > 0) {
        g.roundRect(-width / 2 + 3, drawTop + 1, width - 6, drawHeight - 1, 2);
        g.fill({ color, alpha: shimmer });
      }
    }

    // Draw a glowing line at the base tier threshold
    if (baseFillHeight > 0) {
      const baseLineY = height - baseFillHeight;
      g.moveTo(-width / 2 + 2, baseLineY);
      g.lineTo(width / 2 - 2, baseLineY);
      g.stroke({ color: 0xffffff, width: 2, alpha: 0.6 });
    }

    // Sparkle overlay when sprinting
    this.drawSparkles(minVisibleHeight);

    // Draw rocket at top of fill
    const topColor = this.getColorForMultiplier(current.multiplier);
    this.drawRocket(minVisibleHeight, topColor);
  }

  /**
   * Draw animated sparkles on the fill
   */
  private drawSparkles(fillHeight: number): void {
    const g = this.effects;
    const { width, height } = this.config;

    g.clear();

    if (fillHeight <= 5) return;

    // More sparkles now that gauge is more visible
    // Scale sparkle count based on tier progress
    const { current } = getSprintTier(this.sprintTime);
    const baseSparkles = current.multiplier >= 1.2 ? 4 : 2;
    const sparkleCount = Math.min(8, baseSparkles + Math.floor(fillHeight / 25));

    for (let i = 0; i < sparkleCount; i++) {
      // Pseudo-random position based on time and index
      const phase = this.animTime * 2 + i * 1.7;
      const yOffset = (Math.sin(phase * 0.7 + i) * 0.5 + 0.5) * fillHeight;
      const xOffset = Math.sin(phase + i * 2.3) * (width * 0.3);

      const sparkleY = height - yOffset;
      const sparkleX = xOffset;

      // Twinkling alpha
      const twinkle = Math.sin(this.animTime * 10 + i * 3) * 0.5 + 0.5;

      if (twinkle > 0.3) {
        this.drawSparkle(g, sparkleX, sparkleY, 3 + twinkle * 2, 0xffffff, twinkle * 0.8);
      }
    }

    // Celebration sparkles when tier up
    if (this.celebrationTimer > 0) {
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2 + this.animTime * 3;
        const dist = 20 + (1 - this.celebrationTimer) * 30;
        const x = Math.cos(angle) * dist;
        const y = height - fillHeight + Math.sin(angle) * dist;

        this.drawSparkle(g, x, y, 5, 0xffff00, this.celebrationTimer);
      }
    }
  }

  /**
   * Draw a 4-pointed sparkle
   */
  private drawSparkle(g: Graphics, cx: number, cy: number, size: number, color: number, alpha: number): void {
    const inner = size * 0.3;

    g.moveTo(cx, cy - size);
    g.lineTo(cx + inner, cy - inner);
    g.lineTo(cx + size, cy);
    g.lineTo(cx + inner, cy + inner);
    g.lineTo(cx, cy + size);
    g.lineTo(cx - inner, cy + inner);
    g.lineTo(cx - size, cy);
    g.lineTo(cx - inner, cy - inner);
    g.closePath();
    g.fill({ color, alpha });
  }

  /**
   * Draw the pulsing target line at the next tier threshold
   * Shows players exactly where they need to reach
   */
  private drawTargetLine(): void {
    const g = this.targetLine;
    const { width, height } = this.config;

    g.clear();

    const { next, progress } = getSprintTier(this.sprintTime);

    // Don't draw if no next tier (at max)
    if (!next) return;

    // Calculate target line position
    const tierIndex = SPRINT_MULTIPLIER_TIERS.findIndex(t => t.threshold === next.threshold);
    const totalTiers = SPRINT_MULTIPLIER_TIERS.length;
    const tierHeight = height / (totalTiers - 1);
    const targetY = height - (tierIndex * tierHeight);

    // Get color for the next tier
    const colorIndex = Math.min(tierIndex, RAINBOW_COLORS.length - 1);
    const targetColor = RAINBOW_COLORS[colorIndex] ?? 0x00ffaa;

    // Pulsing glow effect - more intense when closer to target
    const basePulse = Math.sin(this.animTime * ANIMATION_CONFIG.PULSE_SPEED) * 0.3 + 0.7;
    const excitementBonus = progress > PROGRESS_THRESHOLDS.ALMOST_THERE ? (progress - PROGRESS_THRESHOLDS.ALMOST_THERE) * 2 : 0; // 0-0.4 extra when close
    const pulse = basePulse + excitementBonus * Math.sin(this.animTime * ANIMATION_CONFIG.FAST_PULSE_SPEED) * 0.3;

    // Outer glow (wider, softer) - use rect for cleaner rendering
    g.rect(-width / 2 - 10, targetY - 4, width + 20, 8);
    g.fill({ color: targetColor, alpha: pulse * 0.15 });

    // Middle glow
    g.rect(-width / 2 - 6, targetY - 2, width + 12, 4);
    g.fill({ color: targetColor, alpha: pulse * 0.3 });

    // Core line (bright, thin)
    g.rect(-width / 2 - 2, targetY - 1, width + 4, 2);
    g.fill({ color: 0xffffff, alpha: pulse * 0.8 });

    // Draw animated dots on the line (sweeping effect)
    const dotCount = 3;
    for (let i = 0; i < dotCount; i++) {
      const dotPhase = (this.animTime * 2 + i * (Math.PI * 2 / dotCount)) % (Math.PI * 2);
      const dotX = Math.sin(dotPhase) * (width / 2 + 4);
      const dotAlpha = Math.cos(dotPhase) * 0.5 + 0.5;

      g.circle(dotX, targetY, 3);
      g.fill({ color: 0xffffff, alpha: dotAlpha * pulse });
    }

    // "Almost there" arrow indicators when very close (> 90% progress)
    if (progress > PROGRESS_THRESHOLDS.NEARLY_COMPLETE) {
      const arrowPulse = Math.sin(this.animTime * ANIMATION_CONFIG.SPARKLE_SPEED) * 0.3 + 0.7;
      const arrowY = targetY + 15;

      // Left arrow pointing up (use triangle fill instead of stroke)
      g.moveTo(-width / 2 - 8, arrowY);
      g.lineTo(-width / 2 - 4, arrowY - 8);
      g.lineTo(-width / 2, arrowY);
      g.closePath();
      g.fill({ color: targetColor, alpha: arrowPulse });

      // Right arrow pointing up
      g.moveTo(width / 2 + 8, arrowY);
      g.lineTo(width / 2 + 4, arrowY - 8);
      g.lineTo(width / 2, arrowY);
      g.closePath();
      g.fill({ color: targetColor, alpha: arrowPulse });
    }
  }

  /**
   * Draw excitement particles when close to next tier
   * Creates a joyful anticipation effect
   */
  private drawExcitementEffects(): void {
    const g = this.effects;
    const { width, height } = this.config;

    const { next, progress } = getSprintTier(this.sprintTime);

    // Only show excitement when > 60% progress to next tier
    if (!next || progress <= PROGRESS_THRESHOLDS.EXCITEMENT_START) return;

    // Calculate excitement intensity (0 at 60%, 1 at 100%)
    const intensity = (progress - PROGRESS_THRESHOLDS.EXCITEMENT_START) / (1 - PROGRESS_THRESHOLDS.EXCITEMENT_START);

    // Get target line position for particle spawning
    const tierIndex = SPRINT_MULTIPLIER_TIERS.findIndex(t => t.threshold === next.threshold);
    const totalTiers = SPRINT_MULTIPLIER_TIERS.length;
    const tierHeight = height / (totalTiers - 1);
    const targetY = height - (tierIndex * tierHeight);

    // Color for the next tier
    const colorIndex = Math.min(tierIndex, RAINBOW_COLORS.length - 1);
    const particleColor = RAINBOW_COLORS[colorIndex] ?? 0x00ffaa;

    // Spawn rising particles from the fill level toward the target
    const particleCount = Math.floor(3 + intensity * 5); // 3-8 particles based on intensity

    for (let i = 0; i < particleCount; i++) {
      // Pseudo-random position based on time and index
      const phase = this.animTime * 3 + i * 1.3;
      const cyclePos = (phase % 2) / 2; // 0-1 cycle position

      // Particle rises from current fill toward target
      const fillHeight = this.calculateCurrentFillHeight();
      const startY = height - fillHeight;
      const particleY = startY + (targetY - startY) * cyclePos;

      // Horizontal wobble
      const wobble = Math.sin(phase * 4) * (width * 0.4);

      // Size and alpha pulse
      const sizePulse = 1 + Math.sin(phase * 8) * 0.3;
      const alphaPulse = (1 - cyclePos) * intensity; // Fade as it rises

      if (alphaPulse > 0.1) {
        // Draw glowing particle
        g.circle(wobble, particleY, 3 * sizePulse);
        g.fill({ color: particleColor, alpha: alphaPulse * 0.6 });

        // Inner bright core
        g.circle(wobble, particleY, 1.5 * sizePulse);
        g.fill({ color: 0xffffff, alpha: alphaPulse * 0.8 });
      }
    }

    // Extra sparkle burst effect when > 90% (almost there!)
    if (progress > PROGRESS_THRESHOLDS.NEARLY_COMPLETE) {
      const burstIntensity = (progress - PROGRESS_THRESHOLDS.NEARLY_COMPLETE) / (1 - PROGRESS_THRESHOLDS.NEARLY_COMPLETE);
      const burstCount = Math.floor(4 + burstIntensity * 4);

      for (let i = 0; i < burstCount; i++) {
        const angle = (this.animTime * 5 + i * (Math.PI * 2 / burstCount)) % (Math.PI * 2);
        const dist = 15 + Math.sin(this.animTime * 8 + i) * 5;
        const sparkleX = Math.cos(angle) * dist;
        const sparkleY = targetY + Math.sin(angle) * dist * 0.5;
        const sparkleAlpha = (Math.sin(this.animTime * 12 + i * 2) * 0.5 + 0.5) * burstIntensity;

        this.drawSparkle(g, sparkleX, sparkleY, 4, 0xffffff, sparkleAlpha);
      }
    }
  }

  /**
   * Calculate the current fill height for particle effects
   */
  private calculateCurrentFillHeight(): number {
    const { height } = this.config;
    const { current, next, progress } = getSprintTier(this.sprintTime);

    const tierIndex = SPRINT_MULTIPLIER_TIERS.findIndex(t => t.threshold === current.threshold);
    const totalTiers = SPRINT_MULTIPLIER_TIERS.length;
    const tierHeight = height / (totalTiers - 1);
    const baseFillHeight = tierIndex * tierHeight;
    const progressFillHeight = next ? progress * tierHeight : 0;

    return baseFillHeight + progressFillHeight;
  }

  /**
   * Get color based on multiplier value
   */
  private getColorForMultiplier(multiplier: number): number {
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.PURPLE) return 0xaa00ff; // Purple
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.PINK) return 0xff00aa; // Pink
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.RED_ORANGE) return 0xff5500; // Red-orange
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.ORANGE) return 0xffaa00; // Orange
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.YELLOW) return 0xffff00; // Yellow
    if (multiplier >= MULTIPLIER_COLOR_THRESHOLDS.LIME) return 0xaaff00; // Lime
    return 0x00ffaa; // Mint
  }

  /**
   * Update the gauge with current sprint time
   * @param dt - Delta time in seconds
   * @param isSprinting - Whether the player is currently sprinting
   * @param sprintTime - Accumulated sprint time in seconds
   * @param score - Optional player score (for free sprint indicator)
   */
  update(dt: number, isSprinting: boolean, sprintTime: number, score?: number): void {
    this.sprintTime = sprintTime;
    this.animTime += dt;

    // Update free sprint status
    this.hasFreeSprint = score !== undefined && score < FREE_SPRINT_SCORE_THRESHOLD;
    if (this.hasFreeSprint) {
      this.freeSprintAnimTime += dt;
    }

    // Decay celebration timer
    if (this.celebrationTimer > 0) {
      this.celebrationTimer = Math.max(0, this.celebrationTimer - dt * 2);
    }

    // Check for tier-up
    const { current } = getSprintTier(this.sprintTime);
    if (current.multiplier > this.prevTier) {
      this.celebrationTimer = 1; // Start celebration
    }
    this.prevTier = current.multiplier;

    // Show gauge when sprinting or have accumulated time
    this.shouldShow = isSprinting || sprintTime > 0.5;

    // Fade in/out
    const targetAlpha = this.shouldShow ? 1 : 0;
    this.alpha += (targetAlpha - this.alpha) * ANIMATION_CONFIG.FADE_SPEED * dt;
    this.container.alpha = this.alpha;
    this.container.visible = this.alpha > 0.01;

    // Update global multiplier badge (runs even when gauge is faded!)
    this.updateGlobalMultiplierBadge();

    if (!this.container.visible) return;

    // Get tier info for various updates
    const { next, progress } = getSprintTier(this.sprintTime);

    // Update fill
    this.updateFill();

    // Draw target line at next tier threshold
    this.drawTargetLine();

    // Draw excitement effects when close to next tier
    this.drawExcitementEffects();

    // Update multiplier text
    this.multiplierText.text = current.label;

    // Color the text based on tier
    const textColor = this.getColorForMultiplier(current.multiplier);
    this.multiplierText.style.fill = textColor;

    // Bouncy scale animation for text
    const baseScale = 1;
    const bounce = current.multiplier >= MULTIPLIER_COLOR_THRESHOLDS.PINK ? Math.sin(this.animTime * ANIMATION_CONFIG.TEXT_BOUNCE_SPEED) * 0.1 : 0;
    const celebration = this.celebrationTimer > 0 ? Math.sin(this.celebrationTimer * ANIMATION_CONFIG.CELEBRATION_SPEED) * 0.2 : 0;
    this.multiplierText.scale.set(baseScale + bounce + celebration);

    // Update label color to match
    this.labelText.style.fill = textColor;

    // Update "time to next tier" text
    this.updateNextTierText(next, progress);

    // Update FREE SPRINT indicator
    this.updateFreeSprintIndicator(isSprinting);
  }

  /**
   * Update the "time to next tier" text display
   * Shows how many seconds until the next tier and what multiplier they'll get
   */
  private updateNextTierText(next: SprintTier | null, progress: number): void {
    if (!next) {
      // At max tier - show MAX! with celebration
      this.nextTierText.visible = true;
      this.nextTierText.text = 'MAX!';
      this.nextTierText.style.fill = 0xaa00ff; // Purple for max

      // Exciting pulsing animation for MAX
      const maxPulse = 1 + Math.sin(this.animTime * 6) * 0.15;
      this.nextTierText.scale.set(maxPulse);
      return;
    }

    // Calculate time remaining to next tier
    const timeRemaining = next.threshold - this.sprintTime;

    // Only show when actively sprinting toward a tier
    if (timeRemaining <= 0 || this.sprintTime <= 0) {
      this.nextTierText.visible = false;
      return;
    }

    this.nextTierText.visible = true;

    // Format time display
    const timeStr = timeRemaining >= 10 ? Math.ceil(timeRemaining).toString() : timeRemaining.toFixed(1);
    this.nextTierText.text = `${timeStr}s → ${next.label}`;

    // Color based on proximity - more exciting colors when close!
    const nextColor = this.getColorForMultiplier(next.multiplier);

    if (progress > PROGRESS_THRESHOLDS.NEARLY_COMPLETE) {
      // Almost there! - Flash between white and next tier color
      const flash = Math.sin(this.animTime * 12) > 0;
      this.nextTierText.style.fill = flash ? 0xffffff : nextColor;

      // Excited bouncing
      const bounce = 1 + Math.sin(this.animTime * ANIMATION_CONFIG.SPARKLE_SPEED) * 0.12;
      this.nextTierText.scale.set(bounce);
    } else if (progress > PROGRESS_THRESHOLDS.CLOSE) {
      // Getting close - use next tier color
      this.nextTierText.style.fill = nextColor;

      // Gentle pulse
      const pulse = 1 + Math.sin(this.animTime * 6) * 0.08;
      this.nextTierText.scale.set(pulse);
    } else {
      // Normal - yellow text
      this.nextTierText.style.fill = 0xffff00;
      this.nextTierText.scale.set(1);
    }
  }

  /**
   * Update the FREE SPRINT indicator visibility and animation
   */
  private updateFreeSprintIndicator(isSprinting: boolean): void {
    // Show free sprint text when sprinting with free sprint active
    const showFreeSprint = this.hasFreeSprint && isSprinting;
    this.freeSprintText.visible = showFreeSprint;

    if (showFreeSprint) {
      // Pulsing scale animation
      const pulse = 1 + Math.sin(this.freeSprintAnimTime * 8) * 0.15;
      this.freeSprintText.scale.set(pulse);

      // Color cycling between green shades (bright green to lime)
      const hueShift = Math.sin(this.freeSprintAnimTime * 4) * 0.5 + 0.5;
      const green = Math.floor(200 + hueShift * 55); // 200-255
      // Correct color: keep in green spectrum (green to lime-yellow)
      this.freeSprintText.style.fill = (Math.floor(hueShift * 100) << 16) | (green << 8) | 0x00;
    }
  }

  /**
   * Update the persistent global multiplier badge
   * This shows even when the gauge is faded (when multiplier > 1x)
   */
  private updateGlobalMultiplierBadge(): void {
    const { current } = getSprintTier(this.sprintTime);
    const multiplier = current.multiplier;

    // Show badge when multiplier is above 1x OR when gauge is hidden but has accumulated time
    const showBadge = multiplier > 1.0 || (this.sprintTime > 0 && this.alpha < 0.5);

    // Badge has its own alpha, independent of gauge fade
    this.globalMultiplierBadge.visible = showBadge;

    if (!showBadge) return;

    // Update badge text
    this.badgeText.text = current.label;

    // Get color for this tier
    const color = this.getColorForMultiplier(multiplier);
    this.badgeText.style.fill = color;

    // Draw badge background with tier color
    const bg = this.badgeBackground;
    bg.clear();

    const textWidth = this.badgeText.width;
    const textHeight = this.badgeText.height;
    const padX = 12;
    const padY = 6;
    const bgWidth = textWidth + padX * 2;
    const bgHeight = textHeight + padY * 2;

    // Glowing background effect
    const glowPulse = 0.5 + Math.sin(this.animTime * 3) * 0.2;

    // Outer glow
    bg.roundRect(-bgWidth / 2 - 4, -bgHeight / 2 - 4, bgWidth + 8, bgHeight + 8, 12);
    bg.fill({ color, alpha: glowPulse * 0.2 });

    // Main background
    bg.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, 8);
    bg.fill({ color: 0x1a1a2e, alpha: 0.9 });
    bg.stroke({ color, width: 2, alpha: 0.8 });

    // Badge scale pulse when at higher tiers
    const scalePulse = multiplier >= MULTIPLIER_COLOR_THRESHOLDS.PINK ? 1 + Math.sin(this.animTime * ANIMATION_CONFIG.PULSE_SPEED) * 0.05 : 1;
    this.globalMultiplierBadge.scale.set(scalePulse);

    // Keep badge fully visible even when gauge fades
    this.globalMultiplierBadge.alpha = 1;
  }

  /**
   * Position the gauge on screen
   */
  onResize(_screenWidth: number, screenHeight: number): void {
    const { padding, height } = this.config;

    // Position on left side of screen, vertically centered (moved more to the right)
    this.container.position.set(padding + 45, screenHeight / 2 - height / 2);

    // Position text below gauge
    this.multiplierText.position.set(0, height + 20);
    this.labelText.position.set(0, height + 42);

    // Position "time to next tier" text below the label
    this.nextTierText.position.set(0, height + 58);

    // Position FREE SPRINT text above gauge
    this.freeSprintText.position.set(0, -25);

    // Position global multiplier badge to the right of the gauge
    this.globalMultiplierBadge.position.set(50, height / 2);
  }

  /**
   * Get the current sprint multiplier value
   */
  getMultiplier(): number {
    const { current } = getSprintTier(this.sprintTime);
    return current.multiplier;
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.background.destroy();
    this.fill.destroy();
    this.effects.destroy();
    this.markers.destroy();
    this.targetLine.destroy();
    this.rocketIcon.destroy();
    this.multiplierText.destroy();
    this.labelText.destroy();
    this.nextTierText.destroy();
    this.freeSprintText.destroy();
    this.badgeBackground.destroy();
    this.badgeText.destroy();
    this.globalMultiplierBadge.destroy({ children: true });
    this.container.destroy({ children: true });
  }
}
